import React, { useEffect } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import useClasses from '../../../hooks/useClasses';
import usePopup from '../../../hooks/usePopup';
import useRoutes from '../../../hooks/useRoutes';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { resetNewMessage } from '../../../store/actions/Messages';
import { MessageEntity, MessagesState } from '../../../types';
import Button from '../../Button';
import Popup from '../../Popup';
import PopupBadge from '../../Popup/Badge';

const MessageContainer = ({ children }) => {
  const { t } = useTranslation();
  const { badges } = useRoutes();
  const dispatch = useTypedDispatch();
  const popup = usePopup('new_message_popup', {
    closeOnOutsideClick: true,
  });

  const { buildClass } = useClasses('MessageContainer');

  const newMessage = useSelector<MessagesState, ImmutableEntity<MessageEntity> & { size: number }>(
    (state) => state.getIn(['messages', 'newMessage'], Immutable.Map()),
  );

  const newMessageBadge = newMessage.getIn(['field_badge', 0, 'entity'], Immutable.Map());
  const newMessageHasBadge = newMessageBadge.size > 0;

  useEffect(() => {
    if (newMessage.size === 0) {
      return;
    }

    popup.open();
  }, [newMessage.size, popup]);

  const onClosePopup = () => {
    // Reset the new message in the state, so we don't trigger the popup again.
    dispatch(resetNewMessage());
    popup.close();
  };

  return (
    <div className={buildClass()}>
      {children}

      {popup.isOpen && (
        <Popup
          {...popup.popupProps}
          title={t(`messages.${newMessageHasBadge ? 'badge_popup' : 'new'}.title`)}
          closeButtonText={t('buttons.close')}
          cta={newMessageHasBadge ? (
            <Button href={badges} onClick={onClosePopup}>
              {t('messages.badge_popup.cta')}
            </Button>
          ) : null}
          onClose={onClosePopup}
        >
          {newMessageHasBadge ? (
            <PopupBadge
              badge={newMessageBadge}
            />
          ) : (
            <WysiwygField
              property="schema:text"
              html={newMessage.getIn(['text', 0, 'value', 1], '')}
            />
          )}
        </Popup>
      )}
    </div>
  );
};

export default MessageContainer;
